/**
 * WizardStepIndicator - Step progress indicator for the wizard
 * Shows circles connected by lines, with active and completed states
 */
import React from 'react';
import { Flex, Box, Circle as ChakraCircle, Text } from '@chakra-ui/react';

export interface WizardStepIndicatorProps {
  currentStep: number;
  totalSteps?: number;
  stepLabels?: string[];
  steps?: { id: string; label: string }[];
  onStepClick?: (index: number) => void;
}

export function WizardStepIndicator({
  currentStep,
  totalSteps: totalStepsProp,
  stepLabels: stepLabelsProp,
  steps,
  onStepClick,
}: WizardStepIndicatorProps) {
  const totalSteps = steps ? steps.length : (totalStepsProp ?? 0);
  const stepLabels = steps ? steps.map(s => s.label) : stepLabelsProp;
  return (
    <Flex align="center" justify="center" py={4} gap={0}>
      {Array.from({ length: totalSteps }, (_, i) => {
        const step = i + 1;
        const isActive = i === currentStep;
        const isCompleted = i < currentStep;

        return (
          <React.Fragment key={step}>
            {/* Step circle */}
            <Flex direction="column" align="center" gap={1}>
              <ChakraCircle
                size="32px"
                bg={isCompleted ? 'brand.500' : isActive ? 'brand.500' : 'gray.200'}
                color={isCompleted || isActive ? 'white' : 'gray.500'}
                fontWeight="600"
                fontSize="sm"
                transition="all 0.2s"
                cursor={onStepClick && isCompleted ? 'pointer' : 'default'}
                onClick={onStepClick && isCompleted ? () => onStepClick(i) : undefined}
              >
                {isCompleted ? '✓' : step}
              </ChakraCircle>
              {stepLabels && stepLabels[i] && (
                <Text
                  fontSize="2xs"
                  color={isActive ? 'brand.500' : isCompleted ? 'gray.700' : 'gray.400'}
                  fontWeight={isActive ? '600' : '400'}
                  textAlign="center"
                  maxW="80px"
                >
                  {stepLabels[i]}
                </Text>
              )}
            </Flex>

            {/* Connecting line */}
            {step < totalSteps && (
              <Box
                flex={1}
                h="2px"
                minW="20px"
                mx={1}
                mt={stepLabels ? '-12px' : 0}
                bg={isCompleted ? 'brand.500' : 'gray.200'}
                transition="all 0.2s"
              />
            )}
          </React.Fragment>
        );
      })}
    </Flex>
  );
}
